import {
  Clock, Play, GitBranch, Bot, Globe, Search,
  Repeat, Mail, Split, Webhook, FileText,
  Building2, UserPlus, User, CircleDollarSign, StopCircle, Briefcase, Megaphone,
  Database, Trash2, Scissors, Braces, Timer, GitFork, Instagram,
  Sparkles, MapPin, FileDown,
} from 'lucide-react';

export const NODE_TYPES = {
  // Triggers
  cron: {
    type: 'cron',
    label: 'On a Schedule',
    icon: Clock,
    category: 'trigger',
    color: '#64748b',
    defaultConfig: { schedule: '', timezone: 'UTC' },
    outputSchema: [],
  },
  manual: {
    type: 'manual',
    label: 'Run Manually',
    icon: Play,
    category: 'trigger',
    color: '#64748b',
    defaultConfig: {},
    outputSchema: [],
  },
  workflowTrigger: {
    type: 'workflowTrigger',
    label: 'When Workflow Finishes',
    icon: GitBranch,
    category: 'trigger',
    color: '#64748b',
    defaultConfig: { sourceWorkflowId: '' },
    outputSchema: [
      { path: 'payload', label: 'Incoming Data', type: 'object' },
    ],
  },
  webhook: {
    type: 'webhook',
    label: 'Incoming Webhook',
    icon: Webhook,
    category: 'trigger',
    color: '#64748b',
    defaultConfig: {},
    outputSchema: [
      { path: 'payload', label: 'Incoming Data', type: 'object' },
    ],
  },
  formSubmission: {
    type: 'formSubmission',
    label: 'When Form is Submitted',
    icon: FileText,
    category: 'trigger',
    color: '#64748b',
    defaultConfig: { formId: '', formVersion: null },
    outputSchema: [],
  },
  recordChange: {
    type: 'recordChange',
    label: 'When Record Changes',
    icon: Database,
    category: 'trigger',
    color: '#64748b',
    defaultConfig: {
      objectDefinitionId: '',
      eventType: 'any',
      watchFields: [],
      conditions: [],
      delaySeconds: null,
    },
    outputSchema: [
      { path: 'record_id', label: 'Record ID', type: 'string' },
      { path: 'object_definition_id', label: 'Object Type ID', type: 'string' },
      { path: 'event_type', label: 'Event Type', type: 'string' },
      { path: 'data', label: 'Record Data', type: 'object' },
      { path: 'changed_fields', label: 'Changed Fields', type: 'object' },
    ],
  },

  // Actions — CRM (Generic)
  createRecord: {
    type: 'createRecord',
    label: 'Create Record',
    icon: Database,
    category: 'action',
    subcategory: 'CRM',
    color: '#6366f1',
    defaultConfig: { objectDefinitionId: '', fieldValues: {} },
    outputSchema: [
      { path: 'record', label: 'New Record', type: 'object' },
      { path: 'record.id', label: 'Record ID', type: 'string' },
      { path: 'record.data', label: 'Record Data', type: 'object' },
    ],
  },
  getRecord: {
    type: 'getRecord',
    label: 'Get Record',
    icon: Database,
    category: 'action',
    subcategory: 'CRM',
    color: '#6366f1',
    defaultConfig: { objectDefinitionId: '', recordId: '' },
    outputSchema: [
      { path: 'record', label: 'Record', type: 'object' },
      { path: 'record.id', label: 'Record ID', type: 'string' },
      { path: 'record.data', label: 'Record Data', type: 'object' },
    ],
  },
  updateRecord: {
    type: 'updateRecord',
    label: 'Update Record',
    icon: Database,
    category: 'action',
    subcategory: 'CRM',
    color: '#6366f1',
    defaultConfig: { objectDefinitionId: '', recordId: '', fieldValues: {} },
    outputSchema: [
      { path: 'record', label: 'Updated Record', type: 'object' },
      { path: 'record.id', label: 'Record ID', type: 'string' },
      { path: 'record.data', label: 'Record Data', type: 'object' },
    ],
  },
  deleteRecord: {
    type: 'deleteRecord',
    label: 'Delete Record',
    icon: Trash2,
    category: 'action',
    subcategory: 'CRM',
    color: '#6366f1',
    defaultConfig: { objectDefinitionId: '', recordId: '' },
    outputSchema: [
      { path: 'success', label: 'Success', type: 'boolean' },
      { path: 'recordId', label: 'Deleted Record ID', type: 'string' },
    ],
  },
  searchRecords: {
    type: 'searchRecords',
    label: 'Search Records',
    icon: Search,
    category: 'action',
    subcategory: 'CRM',
    color: '#6366f1',
    defaultConfig: { objectDefinitionId: '', filter: { match: 'all', rules: [] }, limit: 10 },
    outputSchema: [
      { path: 'results', label: 'Matches', type: 'array' },
      { path: 'results[].id', label: 'Match IDs', type: 'string' },
      { path: 'results[].data', label: 'Match Data', type: 'object' },
      { path: 'count', label: 'Number of Matches', type: 'number' },
      { path: 'total', label: 'Total Available', type: 'number' },
    ],
  },

  // Actions — CRM (Legacy, hidden from palette)
  createCompany: {
    type: 'createCompany',
    label: 'Create Company',
    icon: Building2,
    category: 'action',
    subcategory: 'CRM',
    color: '#6366f1',
    hidden: true,
    defaultConfig: { name: '', fields: '{}' },
    outputSchema: [
      { path: 'record', label: 'New Company', type: 'object' },
      { path: 'record.id', label: 'Company ID', type: 'string' },
    ],
  },
  getCompany: {
    type: 'getCompany',
    label: 'Find Company',
    icon: Building2,
    category: 'action',
    subcategory: 'CRM',
    color: '#6366f1',
    hidden: true,
    defaultConfig: { companyId: '' },
    outputSchema: [
      { path: 'record', label: 'Company Details', type: 'object' },
      { path: 'record.id', label: 'Company ID', type: 'string' },
    ],
  },
  updateCompany: {
    type: 'updateCompany',
    label: 'Update Company',
    icon: Building2,
    category: 'action',
    subcategory: 'CRM',
    color: '#6366f1',
    hidden: true,
    defaultConfig: { companyId: '', fields: '{}' },
    outputSchema: [
      { path: 'record', label: 'Updated Company', type: 'object' },
      { path: 'record.id', label: 'Company ID', type: 'string' },
    ],
  },
  createPerson: {
    type: 'createPerson',
    label: 'Create Person',
    icon: UserPlus,
    category: 'action',
    subcategory: 'CRM',
    color: '#6366f1',
    hidden: true,
    defaultConfig: { firstName: '', lastName: '', fields: '{}' },
    outputSchema: [
      { path: 'record', label: 'New Person', type: 'object' },
      { path: 'record.id', label: 'Person ID', type: 'string' },
    ],
  },
  getPerson: {
    type: 'getPerson',
    label: 'Find Person',
    icon: User,
    category: 'action',
    subcategory: 'CRM',
    color: '#6366f1',
    hidden: true,
    defaultConfig: { personId: '' },
    outputSchema: [
      { path: 'record', label: 'Person Details', type: 'object' },
      { path: 'record.id', label: 'Person ID', type: 'string' },
    ],
  },
  updatePerson: {
    type: 'updatePerson',
    label: 'Update Person',
    icon: User,
    category: 'action',
    subcategory: 'CRM',
    color: '#6366f1',
    hidden: true,
    defaultConfig: { personId: '', fields: '{}' },
    outputSchema: [
      { path: 'record', label: 'Updated Person', type: 'object' },
      { path: 'record.id', label: 'Person ID', type: 'string' },
    ],
  },
  createDeal: {
    type: 'createDeal',
    label: 'Create Deal',
    icon: CircleDollarSign,
    category: 'action',
    subcategory: 'CRM',
    color: '#6366f1',
    hidden: true,
    defaultConfig: { name: '', companyId: '', pipelineId: '', stageId: '', ownerId: '', value: '' },
    outputSchema: [
      { path: 'record', label: 'New Deal', type: 'object' },
      { path: 'record.id', label: 'Deal ID', type: 'string' },
    ],
  },
  getDeal: {
    type: 'getDeal',
    label: 'Find Deal',
    icon: CircleDollarSign,
    category: 'action',
    subcategory: 'CRM',
    color: '#6366f1',
    hidden: true,
    defaultConfig: { dealId: '' },
    outputSchema: [
      { path: 'record', label: 'Deal Details', type: 'object' },
      { path: 'record.id', label: 'Deal ID', type: 'string' },
    ],
  },
  updateDeal: {
    type: 'updateDeal',
    label: 'Update Deal',
    icon: CircleDollarSign,
    category: 'action',
    subcategory: 'CRM',
    color: '#6366f1',
    hidden: true,
    defaultConfig: { dealId: '', fields: '{}' },
    outputSchema: [
      { path: 'record', label: 'Updated Deal', type: 'object' },
      { path: 'record.id', label: 'Deal ID', type: 'string' },
    ],
  },
  searchCRM: {
    type: 'searchCRM',
    label: 'Search Contacts & Companies',
    icon: Search,
    category: 'action',
    subcategory: 'CRM',
    color: '#6366f1',
    hidden: true,
    defaultConfig: { recordType: 'company', query: '', limit: 10 },
    outputSchema: [
      { path: 'results', label: 'Matches', type: 'array' },
      { path: 'results[].id', label: 'Match IDs', type: 'string' },
      { path: 'results[].name', label: 'Match Names', type: 'string' },
      { path: 'count', label: 'Number of Matches', type: 'number' },
    ],
  },

  // Actions — AI
  callLLM: {
    type: 'callLLM',
    label: 'Ask AI',
    icon: Bot,
    category: 'action',
    subcategory: 'AI',
    color: '#8b5cf6',
    defaultConfig: { model: 'claude-sonnet', systemPrompt: '', userPrompt: '' },
    outputSchema: [
      { path: 'output', label: 'AI Response', type: 'string' },
      { path: 'model', label: 'Model Used', type: 'string' },
    ],
  },

  // Actions — Web
  fetchWebsite: {
    type: 'fetchWebsite',
    label: 'HTTP Request',
    icon: Globe,
    category: 'action',
    subcategory: 'Web',
    color: '#0ea5e9',
    defaultConfig: { url: '', method: 'GET', headers: '' },
    outputSchema: [
      { path: 'body', label: 'Response', type: 'string' },
      { path: 'statusCode', label: 'Status Code', type: 'number' },
      { path: 'headers', label: 'Response Headers', type: 'object' },
    ],
  },
  getWebsiteContent: {
    type: 'getWebsiteContent',
    label: 'Get Website Content',
    icon: FileDown,
    category: 'action',
    subcategory: 'Web',
    color: '#0ea5e9',
    defaultConfig: { url: '', waitUntil: 'load', waitForSelector: '', userAgent: '' },
    outputSchema: [
      { path: 'markdown', label: 'Page as Markdown', type: 'string' },
      { path: 'url', label: 'Source URL', type: 'string' },
      { path: 'success', label: 'Success', type: 'boolean' },
    ],
  },
  // Actions — Prospecting
  linkedinPersonFinder: {
    type: 'linkedinPersonFinder',
    label: 'LinkedIn Person Finder',
    icon: User,
    category: 'action',
    subcategory: 'Prospecting',
    color: '#0a66c2',
    defaultConfig: {
      firstName: '',
      lastName: '',
      profileScraperMode: 'Short',
      strictSearch: true,
      maxPages: 1,
      maxItems: 5,
      locations: '',
      currentCompanies: '',
      pastCompanies: '',
      schools: '',
      currentJobTitles: '',
      industryIds: '',
      includeRawItems: false,
    },
    outputSchema: [
      { path: 'profiles', label: 'Profiles', type: 'array' },
      { path: 'profiles[].fullName', label: 'Full Name', type: 'string' },
      { path: 'profiles[].firstName', label: 'First Name', type: 'string' },
      { path: 'profiles[].lastName', label: 'Last Name', type: 'string' },
      { path: 'profiles[].headline', label: 'Headline', type: 'string' },
      { path: 'profiles[].currentTitle', label: 'Current Title', type: 'string' },
      { path: 'profiles[].currentCompany', label: 'Current Company', type: 'string' },
      { path: 'profiles[].location', label: 'Location', type: 'string' },
      { path: 'profiles[].linkedinUrl', label: 'LinkedIn URL', type: 'string' },
      { path: 'profiles[].email', label: 'Email', type: 'string' },
      { path: 'count', label: 'Profile Count', type: 'number' },
      { path: 'rawItems', label: 'Raw Apify Items (optional)', type: 'array' },
      { path: 'search', label: 'Search Metadata', type: 'object' },
    ],
  },
  linkedinCompanyEmployees: {
    type: 'linkedinCompanyEmployees',
    label: 'LinkedIn Company Employees',
    icon: Building2,
    category: 'action',
    subcategory: 'Prospecting',
    color: '#0a66c2',
    defaultConfig: {
      companyUrls: '',
      profileScraperMode: 'Short ($4 per 1k)',
      maxItems: 25,
      locations: '',
      searchQuery: '',
      jobTitles: '',
      industryIds: '',
      includeRawItems: false,
    },
    outputSchema: [
      { path: 'profiles', label: 'Profiles', type: 'array' },
      { path: 'profiles[].fullName', label: 'Full Name', type: 'string' },
      { path: 'profiles[].firstName', label: 'First Name', type: 'string' },
      { path: 'profiles[].lastName', label: 'Last Name', type: 'string' },
      { path: 'profiles[].headline', label: 'Headline', type: 'string' },
      { path: 'profiles[].currentTitle', label: 'Current Title', type: 'string' },
      { path: 'profiles[].currentCompany', label: 'Current Company', type: 'string' },
      { path: 'profiles[].location', label: 'Location', type: 'string' },
      { path: 'profiles[].linkedinUrl', label: 'LinkedIn URL', type: 'string' },
      { path: 'profiles[].email', label: 'Email', type: 'string' },
      { path: 'count', label: 'Profile Count', type: 'number' },
      { path: 'rawItems', label: 'Raw Apify Items (optional)', type: 'array' },
      { path: 'search', label: 'Search Metadata', type: 'object' },
    ],
  },
  linkedinJobsSearch: {
    type: 'linkedinJobsSearch',
    label: 'LinkedIn Jobs Search Signals',
    icon: Briefcase,
    category: 'action',
    subcategory: 'Prospecting',
    color: '#0a66c2',
    defaultConfig: {
      searchTerms: '',
      locations: '',
      companyUrls: '',
      maxItems: 25,
      includeRawItems: false,
    },
    outputSchema: [
      { path: 'jobs', label: 'Jobs', type: 'array' },
      { path: 'jobs[].title', label: 'Job Title', type: 'string' },
      { path: 'jobs[].companyName', label: 'Company Name', type: 'string' },
      { path: 'jobs[].companyUrl', label: 'Company URL', type: 'string' },
      { path: 'jobs[].location', label: 'Location', type: 'string' },
      { path: 'jobs[].jobUrl', label: 'Job URL', type: 'string' },
      { path: 'jobs[].postedAt', label: 'Posted At', type: 'string' },
      { path: 'jobs[].description', label: 'Description', type: 'string' },
      { path: 'jobs[].employmentType', label: 'Employment Type', type: 'string' },
      { path: 'companies', label: 'Companies', type: 'array' },
      { path: 'companies[].companyName', label: 'Company Name', type: 'string' },
      { path: 'companies[].companyUrl', label: 'Company URL', type: 'string' },
      { path: 'count', label: 'Job Count', type: 'number' },
      { path: 'rawItems', label: 'Raw Apify Items (optional)', type: 'array' },
      { path: 'search', label: 'Search Metadata', type: 'object' },
    ],
  },
  googleSearchResults: {
    type: 'googleSearchResults',
    label: 'Google Search Results / SERP Monitor',
    icon: Search,
    category: 'action',
    subcategory: 'Prospecting',
    color: '#1a73e8',
    defaultConfig: {
      queries: '',
      countryCode: 'US',
      languageCode: 'en',
      maxPagesPerQuery: 1,
      includePeopleAlsoAsk: true,
      includeAds: false,
      includeAiOverview: false,
      includeRawItems: false,
    },
    outputSchema: [
      { path: 'results', label: 'Search Results', type: 'array' },
      { path: 'results[].query', label: 'Query', type: 'string' },
      { path: 'results[].rank', label: 'Rank', type: 'number' },
      { path: 'results[].title', label: 'Title', type: 'string' },
      { path: 'results[].url', label: 'URL', type: 'string' },
      { path: 'results[].displayUrl', label: 'Display URL', type: 'string' },
      { path: 'results[].snippet', label: 'Snippet', type: 'string' },
      { path: 'results[].type', label: 'Result Type', type: 'string' },
      { path: 'questions', label: 'People Also Ask Questions', type: 'array' },
      { path: 'ads', label: 'Paid Ads', type: 'array' },
      { path: 'count', label: 'Result Count', type: 'number' },
      { path: 'rawItems', label: 'Raw Apify Items (optional)', type: 'array' },
      { path: 'search', label: 'Search Metadata', type: 'object' },
    ],
  },
  googleMapsReviews: {
    type: 'googleMapsReviews',
    label: 'Google Maps Reviews Monitor',
    icon: MapPin,
    category: 'action',
    subcategory: 'Prospecting',
    color: '#34a853',
    defaultConfig: {
      placeUrls: '',
      maxReviews: 25,
      sort: 'newest',
      minRating: '',
      includeRawItems: false,
    },
    outputSchema: [
      { path: 'reviews', label: 'Reviews', type: 'array' },
      { path: 'reviews[].placeName', label: 'Place Name', type: 'string' },
      { path: 'reviews[].rating', label: 'Rating', type: 'number' },
      { path: 'reviews[].text', label: 'Review Text', type: 'string' },
      { path: 'reviews[].publishedAt', label: 'Published At', type: 'string' },
      { path: 'reviews[].reviewerName', label: 'Reviewer Name', type: 'string' },
      { path: 'reviews[].reviewUrl', label: 'Review URL', type: 'string' },
      { path: 'reviews[].ownerResponse', label: 'Owner Response', type: 'string' },
      { path: 'averageRating', label: 'Average Rating', type: 'number' },
      { path: 'count', label: 'Review Count', type: 'number' },
      { path: 'rawItems', label: 'Raw Apify Items (optional)', type: 'array' },
      { path: 'search', label: 'Search Metadata', type: 'object' },
    ],
  },
  facebookAdsLibrarySearch: {
    type: 'facebookAdsLibrarySearch',
    label: 'Facebook Ads Library Monitor',
    icon: Megaphone,
    category: 'action',
    subcategory: 'Prospecting',
    color: '#1877f2',
    defaultConfig: {
      searchTerms: '',
      pageIds: '',
      adLibraryUrls: '',
      countries: 'ALL',
      activeStatus: 'active',
      maxItems: 25,
      includeRawItems: false,
    },
    outputSchema: [
      { path: 'ads', label: 'Ads', type: 'array' },
      { path: 'ads[].adId', label: 'Ad ID', type: 'string' },
      { path: 'ads[].advertiserName', label: 'Advertiser Name', type: 'string' },
      { path: 'ads[].adText', label: 'Ad Text', type: 'string' },
      { path: 'ads[].landingPageUrl', label: 'Landing Page URL', type: 'string' },
      { path: 'ads[].startedAt', label: 'Start Date', type: 'string' },
      { path: 'ads[].endedAt', label: 'End Date', type: 'string' },
      { path: 'ads[].isActive', label: 'Is Active', type: 'boolean' },
      { path: 'ads[].platforms', label: 'Platforms', type: 'array' },
      { path: 'ads[].imageUrls', label: 'Image URLs', type: 'array' },
      { path: 'ads[].videoUrls', label: 'Video URLs', type: 'array' },
      { path: 'ads[].adSnapshotUrl', label: 'Ad Snapshot URL', type: 'string' },
      { path: 'count', label: 'Ad Count', type: 'number' },
      { path: 'rawItems', label: 'Raw Apify Items (optional)', type: 'array' },
      { path: 'search', label: 'Search Metadata', type: 'object' },
    ],
  },
  instagramProfileSnapshot: {
    type: 'instagramProfileSnapshot',
    label: 'Instagram Profile Snapshot',
    icon: Instagram,
    category: 'action',
    subcategory: 'Prospecting',
    color: '#e1306c',
    defaultConfig: {
      profileUrlsOrUsernames: '',
      includeAboutSection: false,
      includeRecentPosts: true,
      maxPosts: 6,
      maxItems: 25,
      includeRawItems: false,
    },
    outputSchema: [
      { path: 'profiles', label: 'Profiles', type: 'array' },
      { path: 'profiles[].username', label: 'Username', type: 'string' },
      { path: 'profiles[].fullName', label: 'Full Name', type: 'string' },
      { path: 'profiles[].bio', label: 'Bio', type: 'string' },
      { path: 'profiles[].website', label: 'Website', type: 'string' },
      { path: 'profiles[].followersCount', label: 'Followers', type: 'number' },
      { path: 'profiles[].followsCount', label: 'Following', type: 'number' },
      { path: 'profiles[].postsCount', label: 'Posts', type: 'number' },
      { path: 'profiles[].isVerified', label: 'Verified', type: 'boolean' },
      { path: 'profiles[].profileUrl', label: 'Profile URL', type: 'string' },
      { path: 'profiles[].latestPosts', label: 'Latest Posts', type: 'array' },
      { path: 'count', label: 'Profile Count', type: 'number' },
      { path: 'rawItems', label: 'Raw Apify Items (optional)', type: 'array' },
      { path: 'search', label: 'Search Metadata', type: 'object' },
    ],
  },

  // Actions — Communication
  sendEmail: {
    type: 'sendEmail',
    label: 'Send Email',
    icon: Mail,
    category: 'action',
    subcategory: 'Communication',
    color: '#ef4444',
    defaultConfig: { to: '', subject: '', body: '' },
    outputSchema: [
      { path: 'messageId', label: 'Message ID', type: 'string' },
    ],
  },

  // Actions — Gmail (requires Google connection)
  gmailSendEmail: {
    type: 'gmailSendEmail',
    label: 'Gmail: Send Email',
    icon: Mail,
    category: 'action',
    subcategory: 'Google',
    color: '#ea4335',
    defaultConfig: { connectionId: '', to: '', subject: '', body: '', cc: '', bcc: '' },
    outputSchema: [
      { path: 'messageId', label: 'Message ID', type: 'string' },
      { path: 'threadId', label: 'Thread ID', type: 'string' },
    ],
  },
  gmailReadEmail: {
    type: 'gmailReadEmail',
    label: 'Gmail: Read Emails',
    icon: Mail,
    category: 'action',
    subcategory: 'Google',
    color: '#ea4335',
    defaultConfig: { connectionId: '', query: '', maxResults: 5 },
    outputSchema: [
      { path: 'messages', label: 'Email Messages', type: 'array' },
      { path: 'messages[].id', label: 'Message ID', type: 'string' },
      { path: 'messages[].from', label: 'Sender', type: 'string' },
      { path: 'messages[].to', label: 'Recipient', type: 'string' },
      { path: 'messages[].subject', label: 'Subject', type: 'string' },
      { path: 'messages[].body', label: 'Body', type: 'string' },
      { path: 'messages[].date', label: 'Date', type: 'string' },
      { path: 'resultCount', label: 'Result Count', type: 'number' },
    ],
  },
  gmailReplyEmail: {
    type: 'gmailReplyEmail',
    label: 'Gmail: Reply to Email',
    icon: Mail,
    category: 'action',
    subcategory: 'Google',
    color: '#ea4335',
    defaultConfig: { connectionId: '', messageId: '', body: '' },
    outputSchema: [
      { path: 'messageId', label: 'Reply Message ID', type: 'string' },
      { path: 'threadId', label: 'Thread ID', type: 'string' },
    ],
  },

  // Actions — Logic
  iterate: {
    type: 'iterate',
    label: 'Loop Through List',
    icon: Repeat,
    category: 'action',
    subcategory: 'Logic',
    color: '#f59e0b',
    defaultConfig: { sourceField: '', max_iterations: '', continue_on_error: false },
    outputSchema: [
      // Per-iteration fields — available inside the loop body
      { path: 'item', label: 'Current Item (whole object)', type: 'object' },
      { path: 'item.name', label: 'Item → Name', type: 'string' },
      { path: 'item.id', label: 'Item → ID', type: 'string' },
      { path: 'item.email', label: 'Item → Email', type: 'string' },
      { path: 'item.phone', label: 'Item → Phone', type: 'string' },
      { path: 'item.address', label: 'Item → Address', type: 'string' },
      { path: 'item.website', label: 'Item → Website', type: 'string' },
      { path: 'item.rating', label: 'Item → Rating', type: 'number' },
      { path: 'item.placeId', label: 'Item → Place ID', type: 'string' },
      { path: 'index', label: 'Position in List (0-based)', type: 'number' },
    ],
  },
  conditional: {
    type: 'conditional',
    label: 'If / Otherwise',
    icon: Split,
    category: 'action',
    subcategory: 'Logic',
    color: '#f59e0b',
    defaultConfig: { condition: '' },
    outputSchema: [],
  },
  stop: {
    type: 'stop',
    label: 'End Workflow',
    icon: StopCircle,
    category: 'action',
    subcategory: 'Logic',
    color: '#ef4444',
    defaultConfig: { reason: '' },
    outputSchema: [],
  },

  // Actions — Logic (Data Transformation)
  extractData: {
    type: 'extractData',
    label: 'Extract Data',
    icon: Scissors,
    category: 'action',
    subcategory: 'Logic',
    color: '#f59e0b',
    defaultConfig: { source: '', mode: 'keyPath', pattern: '' },
    outputSchema: [
      { path: 'value', label: 'Extracted Value', type: 'string' },
      { path: 'matched', label: 'Matched', type: 'boolean' },
    ],
  },
  parseJson: {
    type: 'parseJson',
    label: 'Parse JSON',
    icon: Braces,
    category: 'action',
    subcategory: 'Logic',
    color: '#f59e0b',
    defaultConfig: { source: '' },
    outputSchema: [
      { path: 'parsed', label: 'Parsed Object', type: 'object' },
    ],
  },

  // Actions — Logic (Flow Control)
  delay: {
    type: 'delay',
    label: 'Delay / Wait',
    icon: Timer,
    category: 'action',
    subcategory: 'Logic',
    color: '#f59e0b',
    defaultConfig: { duration: 5, unit: 'minutes' },
    outputSchema: [
      { path: 'resumedAt', label: 'Resumed At', type: 'string' },
    ],
  },
  router: {
    type: 'router',
    label: 'Router',
    icon: GitFork,
    category: 'action',
    subcategory: 'Logic',
    color: '#f59e0b',
    defaultConfig: {
      rules: [
        { label: 'Route A', field: '', operator: 'equals', value: '' },
      ],
    },
    outputSchema: [
      { path: 'matchedBranch', label: 'Matched Branch', type: 'string' },
      { path: 'matchedIndex', label: 'Matched Index', type: 'number' },
    ],
  },

  // Actions — AI (Research)
  perplexityResearch: {
    type: 'perplexityResearch',
    label: 'Perplexity Research',
    icon: Sparkles,
    category: 'action',
    subcategory: 'AI',
    color: '#8b5cf6',
    defaultConfig: { query: '', model: 'sonar', systemPrompt: '' },
    outputSchema: [
      { path: 'answer', label: 'Answer', type: 'string' },
      { path: 'citations', label: 'Citations', type: 'array' },
      { path: 'model', label: 'Model Used', type: 'string' },
    ],
  },

  // Actions — Web (Research)
  webSearch: {
    type: 'webSearch',
    label: 'Web Search',
    icon: Search,
    category: 'action',
    subcategory: 'Web',
    color: '#0ea5e9',
    defaultConfig: { query: '', maxResults: 5 },
    outputSchema: [
      { path: 'results', label: 'Results', type: 'array' },
      { path: 'results[].title', label: 'Title', type: 'string' },
      { path: 'results[].url', label: 'URL', type: 'string' },
      { path: 'results[].snippet', label: 'Snippet', type: 'string' },
      { path: 'resultCount', label: 'Result Count', type: 'number' },
      { path: 'summary', label: 'AI Summary', type: 'string' },
    ],
  },
  googlePlacesSearch: {
    type: 'googlePlacesSearch',
    label: 'Google Places Search',
    icon: MapPin,
    category: 'action',
    subcategory: 'Web',
    color: '#0ea5e9',
    defaultConfig: { query: '', location: '', radius: 10, maxResults: 20, typeFilter: '' },
    outputSchema: [
      { path: 'results', label: 'Results', type: 'array' },
      { path: 'results[].name', label: 'Business Name', type: 'string' },
      { path: 'results[].address', label: 'Address', type: 'string' },
      { path: 'results[].phone', label: 'Phone', type: 'string' },
      { path: 'results[].website', label: 'Website', type: 'string' },
      { path: 'results[].rating', label: 'Rating', type: 'number' },
      { path: 'results[].placeId', label: 'Place ID', type: 'string' },
      { path: 'results[].types', label: 'Types', type: 'array' },
      { path: 'results[].location.lat', label: 'Latitude', type: 'number' },
      { path: 'results[].location.lng', label: 'Longitude', type: 'number' },
      { path: 'resultCount', label: 'Result Count', type: 'number' },
    ],
  },
};

export const TRIGGER_TYPES = Object.values(NODE_TYPES).filter(n => n.category === 'trigger');
export const ACTION_TYPES = Object.values(NODE_TYPES).filter(n => n.category === 'action');
